import {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  type KeyboardEvent,
  type FormEvent,
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useI18n } from '../../../i18n';
import { parseLocationContext, getSuggestions } from './chat-context';
import { useActiveDocument } from '../../../context/ActiveDocumentContext';
import { useStreamChat } from './hooks/useStreamChat';
import { request } from '../../shared/services/api';

// ═══════════════════════════════════════════════════════════════
// VIAChatButton — Floating chat button + resizable side panel
// ═══════════════════════════════════════════════════════════════

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

// ─── SVG Icon Components (stroke only, 24x24) ───────────────

function IconLightning({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

function IconClock({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function IconPlus({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function IconDash({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function IconExpand({ size = 14, expanded = false }: { size?: number; expanded?: boolean }) {
  return expanded ? (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 2v4H2M10 14v-4h4M14 6h-4V2M2 10h4v4" />
    </svg>
  ) : (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 6V2h4M14 10v4h-4M10 2h4v4M6 14H2v-4" />
    </svg>
  );
}

function IconSend({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function IconInfo({ size = 9 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function IconGrid({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </svg>
  );
}

function IconFileText({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function IconWrench({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}

function IconLayout({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  );
}

// Map suggestion index to SVG icon component
const CHIP_ICONS = [IconGrid, IconFileText, IconWrench, IconLayout];

// ─── Markdown renderer ──────────────────────────────────────

function renderMessageContent(text: string, role: string, navigate: (path: string) => void) {
  if (role === 'user') return text;

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => (
          <h3 className="text-base font-bold mt-3 mb-1" style={{ color: '#1F3864' }}>
            {children}
          </h3>
        ),
        h2: ({ children }) => (
          <h4 className="text-sm font-bold mt-2 mb-1" style={{ color: '#2F5496' }}>
            {children}
          </h4>
        ),
        h3: ({ children }) => (
          <h5 className="text-sm font-semibold mt-2 mb-1" style={{ color: '#4472C4' }}>
            {children}
          </h5>
        ),
        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
        strong: ({ children }) => (
          <strong style={{ fontWeight: 600, color: '#1F3864' }}>{children}</strong>
        ),
        em: ({ children }) => <em className="italic">{children}</em>,
        ul: ({ children }) => (
          <ul className="list-disc list-outside ml-4 mb-2 space-y-0.5">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal list-outside ml-4 mb-2 space-y-0.5">{children}</ol>
        ),
        li: ({ children }) => <li className="text-sm leading-relaxed">{children}</li>,
        code: ({ children, className }) => {
          if (className?.includes('language-')) {
            return (
              <pre
                style={{
                  background: 'rgba(47,84,150,0.08)',
                  border: '1px solid #E5E7EB',
                  borderRadius: 6,
                  padding: 8,
                  margin: '8px 0',
                  overflowX: 'auto',
                  fontSize: 11,
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                <code>{children}</code>
              </pre>
            );
          }
          return (
            <code
              style={{
                background: 'rgba(47,84,150,0.08)',
                color: '#2F5496',
                padding: '1px 4px',
                borderRadius: 3,
                fontSize: 11,
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {children}
            </code>
          );
        },
        pre: ({ children }) => <>{children}</>,
        table: ({ children }) => (
          <div className="overflow-x-auto my-2">
            <table className="text-xs border-collapse w-full">{children}</table>
          </div>
        ),
        thead: ({ children }) => (
          <thead style={{ background: '#1F3864', color: '#fff' }}>{children}</thead>
        ),
        th: ({ children }) => (
          <th className="px-2 py-1 text-left font-semibold border border-gray-300">{children}</th>
        ),
        td: ({ children }) => <td className="px-2 py-1 border border-gray-200">{children}</td>,
        a: ({ href, children }) => (
          <a
            href={href}
            onClick={(e) => {
              if (href?.startsWith('/')) {
                e.preventDefault();
                navigate(href);
              }
            }}
            style={{ color: '#2F5496', textDecoration: 'underline', cursor: 'pointer' }}
          >
            {children}
          </a>
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

// ═══════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════

// Tiempo (ms) que se espera tras un cambio de contextKey antes de mostrar el
// banner "Nuevo contexto". Evita parpadeos al abrir/cerrar panel lateral o
// navegar rápido entre documentos. Si algún tenant reporta lentitud, centralizado.
const CONTEXT_CHANGE_DEBOUNCE_MS = 300;

export function VIAChatButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();

  // ─── State ─────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [panelWidth, setPanelWidth] = useState(700);
  const [expanded, setExpanded] = useState(false);
  const [activeConversacionId, setActiveConversacionId] = useState<string | null>(null);
  const [historialList, setHistorialList] = useState<
    Array<{
      id: string;
      titulo: string | null;
      actualizadoEn: string;
      _count: { mensajes: number };
    }>
  >([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const quickActionsRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  // ─── Context parsing ───────────────────────────────────────
  const ctx = useMemo(
    () => parseLocationContext(location.pathname, location.search),
    [location.pathname, location.search],
  );

  // Entidad activa publicada por las Detail/Viewer pages — prioriza sobre el
  // contexto derivado de la URL cuando existe (p.ej. documento abierto desde el
  // panel lateral del proceso, URL sigue siendo la del proceso). 389.M7 extendió
  // este context a persona/puesto/proceso además de documento.
  const {
    codigoDocumento: activeDocCodigo,
    tituloDocumento: activeDocTitulo,
    personaId: activePersonaId,
    personaNombre: activePersonaNombre,
    puestoId: activePuestoId,
    puestoTitulo: activePuestoTitulo,
    procesoCodigo: activeProcesoCodigo,
    procesoNombre: activeProcesoNombre,
  } = useActiveDocument();

  // Resolve entity name/code from UUID or group number
  const [entidadNombre, setEntidadNombre] = useState('');
  const [entidadCodigo, setEntidadCodigo] = useState('');

  useEffect(() => {
    type ProcesosResponse = { data?: unknown; datos?: unknown };
    if (ctx.modulo === 'process-map' && ctx.vista === 'nivel3' && ctx.entidadId) {
      request<ProcesosResponse>('/procesos')
        .then((json) => {
          const all = (json.data ?? json.datos ?? json) as Array<{
            id: string;
            codigo: string;
            nombre: string;
          }>;
          const found = all.find((p) => p.id === ctx.entidadId);
          if (found) {
            setEntidadCodigo(found.codigo);
            setEntidadNombre(found.nombre);
          }
        })
        .catch(() => {});
    } else if (ctx.modulo === 'process-map' && ctx.vista === 'nivel2' && ctx.entidadId) {
      const grupoNum = parseInt(ctx.entidadId, 10);
      const tip = ctx.extra;
      request<ProcesosResponse>('/procesos')
        .then((json) => {
          const all = (json.data ?? json.datos ?? json) as Array<{
            grupoNumero: number;
            grupoNombre: string;
            tipologia: string;
          }>;
          const found = all.find(
            (p) => p.grupoNumero === grupoNum && (!tip || p.tipologia === tip),
          );
          if (found) {
            setEntidadCodigo(`Gr.${String(grupoNum).padStart(2, '0')}`);
            setEntidadNombre(found.grupoNombre);
          }
        })
        .catch(() => {});
    } else if (ctx.entidadId && ctx.modulo === 'document') {
      setEntidadCodigo(ctx.entidadId);
      setEntidadNombre('');
    } else {
      setEntidadNombre('');
      setEntidadCodigo('');
    }
  }, [ctx.entidadId, ctx.modulo, ctx.vista, ctx.extra]);

  const suggestions = useMemo(() => {
    const moduloEfectivo = activeDocCodigo ? 'document' : ctx.modulo;
    const vistaEfectiva = activeDocCodigo ? 'viewer' : ctx.vista;
    const entidadEfectiva = activeDocCodigo
      ? `${activeDocCodigo}${activeDocTitulo ? ` ${activeDocTitulo}` : ''}`
      : entidadCodigo
        ? `${entidadCodigo}${entidadNombre ? ` ${entidadNombre}` : ''}`
        : undefined;
    return getSuggestions(moduloEfectivo, vistaEfectiva, entidadEfectiva);
  }, [activeDocCodigo, activeDocTitulo, ctx.modulo, ctx.vista, entidadCodigo, entidadNombre]);

  // ─── Context change detection ──────────────────────────────
  const [lastContextKey, setLastContextKey] = useState('');
  const [showContextBanner, setShowContextBanner] = useState(false);
  const contextKey = activeDocCodigo
    ? `document:viewer:${activeDocCodigo}`
    : activePersonaId
      ? `structure:persona-detalle:${activePersonaId}`
      : activePuestoId
        ? `structure:puesto-detalle:${activePuestoId}`
        : activeProcesoCodigo
          ? `process:ficha:${activeProcesoCodigo}`
          : `${ctx.modulo}:${ctx.vista}:${entidadCodigo || ctx.entidadId || ''}`;

  // Debounce del cambio de contextKey para evitar parpadeo del banner cuando
  // el usuario abre/cierra el panel lateral o navega rápidamente entre docs.
  const contextKeyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (contextKeyDebounceRef.current) clearTimeout(contextKeyDebounceRef.current);
    contextKeyDebounceRef.current = setTimeout(() => {
      if (lastContextKey && contextKey !== lastContextKey && messages.length > 0) {
        setShowContextBanner(true);
      }
      setLastContextKey(contextKey);
    }, CONTEXT_CHANGE_DEBOUNCE_MS);
    return () => {
      if (contextKeyDebounceRef.current) clearTimeout(contextKeyDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey]);

  // 390.M4 — helper centralizado para el prefijo i18n del contexto.
  // Antes había 3 usos con el literal `${t.via.contexto}: ${...}` repetido.
  const formatContextLabel = useCallback(
    (label: string) => `${t.via.contexto ?? 'Contexto'}: ${label}`,
    [t.via.contexto],
  );

  // ─── Context label for display ─────────────────────────────
  const contextLabel = useMemo(() => {
    if (activeDocCodigo) return `${activeDocCodigo}${activeDocTitulo ? ` ${activeDocTitulo}` : ''}`;
    if (activePersonaId) return activePersonaNombre ?? activePersonaId;
    if (activePuestoId) return activePuestoTitulo ?? activePuestoId;
    if (activeProcesoCodigo)
      return `${activeProcesoCodigo}${activeProcesoNombre ? ` ${activeProcesoNombre}` : ''}`;
    if (entidadCodigo) return `${entidadCodigo}${entidadNombre ? ` ${entidadNombre}` : ''}`;
    if (ctx.entidadId) return ctx.entidadId;
    if (ctx.modulo === 'general') return 'General';
    return ctx.modulo.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }, [
    activeDocCodigo,
    activeDocTitulo,
    activePersonaId,
    activePersonaNombre,
    activePuestoId,
    activePuestoTitulo,
    activeProcesoCodigo,
    activeProcesoNombre,
    entidadCodigo,
    entidadNombre,
    ctx.entidadId,
    ctx.modulo,
  ]);

  // ─── Scroll to bottom ─────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ─── Focus textarea on open ────────────────────────────────
  useEffect(() => {
    if (open) {
      setTimeout(() => textareaRef.current?.focus(), 200);
    } else {
      // Al cerrar: reset del modo expandido para que al reabrir empiece en tamaño normal
      setExpanded(false);
    }
  }, [open]);

  // ─── Auto-resize textarea ─────────────────────────────────
  const handleTextareaChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = '20px';
    el.style.height = `${Math.min(el.scrollHeight, 72)}px`;
  }, []);

  // ─── Close overlays on click outside ───────────────────────
  useEffect(() => {
    function handleClickOutside(e: globalThis.MouseEvent) {
      if (
        showQuickActions &&
        quickActionsRef.current &&
        !quickActionsRef.current.contains(e.target as Node)
      ) {
        setShowQuickActions(false);
      }
      if (showHistory && historyRef.current && !historyRef.current.contains(e.target as Node)) {
        setShowHistory(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQuickActions, showHistory]);

  // ─── Resize handle ────────────────────────────────────────
  const handleResizeStart = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      const startX = e.clientX;
      const startWidth = panelWidth;

      function onMouseMove(ev: globalThis.MouseEvent) {
        if (!isDragging.current) return;
        const delta = startX - ev.clientX;
        const newWidth = Math.min(700, Math.max(340, startWidth + delta));
        setPanelWidth(newWidth);
      }

      function onMouseUp() {
        isDragging.current = false;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }

      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [panelWidth],
  );

  // ─── Streaming chat hook ───────────────────────────────────
  // Prioridad de contexto (389.M7):
  //   1. Documento activo (abierto desde panel o ruta directa)
  //   2. Persona detalle
  //   3. Puesto detalle
  //   4. Proceso (ficha de caracterización)
  //   5. Contexto derivado de la URL
  const streamCtx = useMemo(() => {
    if (activeDocCodigo) {
      return { modulo: 'document', vista: 'viewer', entidadId: activeDocCodigo, extra: ctx.extra };
    }
    if (activePersonaId) {
      return {
        modulo: 'structure',
        vista: 'persona-detalle',
        entidadId: activePersonaId,
        extra: ctx.extra,
      };
    }
    if (activePuestoId) {
      return {
        modulo: 'structure',
        vista: 'puesto-detalle',
        entidadId: activePuestoId,
        extra: ctx.extra,
      };
    }
    if (activeProcesoCodigo) {
      return {
        modulo: 'process',
        vista: 'ficha',
        entidadId: activeProcesoCodigo,
        extra: ctx.extra,
      };
    }
    return {
      modulo: ctx.modulo,
      vista: ctx.vista,
      entidadId: entidadCodigo || ctx.entidadId,
      extra: ctx.extra,
    };
  }, [
    activeDocCodigo,
    activePersonaId,
    activePuestoId,
    activeProcesoCodigo,
    ctx.modulo,
    ctx.vista,
    ctx.extra,
    entidadCodigo,
    ctx.entidadId,
  ]);
  const stream = useStreamChat({ contexto: streamCtx });

  // Scroll durante streaming (stream ya declarado)
  useEffect(() => {
    if (stream.respuestaParcial) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [stream.respuestaParcial]);

  // ID del último mensaje persistido desde streaming (para mostrar fuentes/sentinel)
  // Usamos state en lugar de ref porque se lee durante render (regla react-hooks/refs
  // de eslint-plugin-react-hooks v7.1+ prohibe ref.current en render)
  const [lastStreamedMsgId, setLastStreamedMsgId] = useState<string | null>(null);

  // Cuando streaming termina, persistir el mensaje en el array
  useEffect(() => {
    if (!stream.isStreaming && loading) {
      if (stream.respuestaParcial) {
        const msgId = `a-${Date.now()}`;
        setLastStreamedMsgId(msgId);
        const finalMsg: ChatMessage = {
          id: msgId,
          role: 'assistant',
          content: stream.respuestaParcial,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, finalMsg]);
      } else if (stream.error) {
        const msgId = `a-${Date.now()}`;
        setLastStreamedMsgId(msgId);
        setMessages((prev) => [
          ...prev,
          {
            id: msgId,
            role: 'assistant',
            content: `Error: ${stream.error}`,
            timestamp: new Date(),
          },
        ]);
      }
      setLoading(false);
      if (stream.conversacionId) setActiveConversacionId(stream.conversacionId);
    }
  }, [stream.isStreaming]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Send message (streaming) ─────────────────────────────
  const handleSend = useCallback(
    async (e?: FormEvent, directText?: string) => {
      e?.preventDefault();
      const text = (directText ?? input).trim();
      if (!text || loading) return;

      const userMsg: ChatMessage = {
        id: `u-${Date.now()}`,
        role: 'user',
        content: text,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setInput('');
      setLoading(true);
      setShowContextBanner(false);

      if (textareaRef.current) {
        textareaRef.current.style.height = '20px';
      }

      stream.enviar(text, activeConversacionId ?? undefined);
    },
    [input, loading, activeConversacionId, stream],
  );

  // ─── Cargar historial al abrir el panel ───────────────────
  useEffect(() => {
    if (open && showHistory) {
      request<{ data?: unknown[] }>('/via/historial')
        .then((json) => {
          if (json?.data) setHistorialList(json.data as typeof historialList);
        })
        .catch(() => {});
    }
  }, [open, showHistory]);

  // ─── Cargar conversación del historial ────────────────────
  const loadConversacion = useCallback((convId: string) => {
    request<{
      data?: { mensajes: Array<{ id: string; rol: string; contenido: string; creadoEn: string }> };
    }>(`/via/historial/${convId}`)
      .then((json) => {
        if (json?.data?.mensajes) {
          const loaded: ChatMessage[] = json.data.mensajes.map((m) => ({
            id: m.id,
            role: m.rol === 'ASISTENTE' ? 'assistant' : 'user',
            content: m.contenido,
            timestamp: new Date(m.creadoEn),
          }));
          setMessages(loaded);
          setActiveConversacionId(convId);
          setShowHistory(false);
        }
      })
      .catch(() => {});
  }, []);

  // ─── Nueva conversación ───────────────────────────────────
  const handleNewConversation = useCallback(() => {
    setMessages([]);
    setActiveConversacionId(null);
    setShowHistory(false);
    stream.cancelar();
  }, [stream]);

  const submitChip = useCallback(
    (text: string) => {
      setInput(text);
      handleSend(undefined, text);
    },
    [handleSend],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════

  return (
    <>
      {/* ═══ PANEL ═══ */}
      <div
        ref={panelRef}
        className={`via-panel fixed z-[9999] flex flex-col bg-white overflow-hidden ${open ? 'via-panel-open' : 'via-panel-closed'}`}
        style={{
          width: expanded
            ? Math.min(
                panelWidth * 2,
                (typeof window !== 'undefined' ? window.innerWidth : 1920) - 48,
              )
            : panelWidth,
          height: expanded ? 'min(1240px, calc(100vh - 48px))' : 'min(620px, calc(100vh - 100px))',
          bottom: 24,
          right: 24,
          borderRadius: 16,
          boxShadow: '0 8px 40px rgba(31,56,100,0.22), 0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #E5E7EB',
          pointerEvents: open ? 'auto' : 'none',
          transition: 'width 0.25s ease, height 0.25s ease',
        }}
      >
        {/* ── Resize Handle ── */}
        <div
          className="via-resize-handle"
          onMouseDown={handleResizeStart}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 6,
            height: '100%',
            cursor: 'ew-resize',
            zIndex: 10,
          }}
        />

        {/* ── HEADER ── */}
        <div
          style={{
            background: 'linear-gradient(135deg, #1F3864, #2F5496)',
            padding: '10px 12px',
            flexShrink: 0,
            position: 'relative',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Imagotipo */}
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <img
                src="/img/Imagotipo-Color.png"
                alt="V·IA"
                style={{ width: '80%', height: '80%', objectFit: 'contain' }}
              />
            </div>

            {/* Title + context pill */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', lineHeight: 1.2 }}>
                V·IA — Asistente de Gobernanza
              </div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  marginTop: 3,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: 'rgba(255,255,255,0.1)',
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: '#33BCE1',
                    flexShrink: 0,
                  }}
                />
                <span
                  data-testid="chat-context-badge"
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {contextLabel}
                  {stream.proveedor && (
                    <>
                      {' '}
                      · {stream.proveedor}
                      {stream.modelo ? ` (${stream.modelo})` : ''}
                    </>
                  )}
                  {stream.embeddingProvider && (
                    <span
                      style={{
                        color: stream.embeddingProvider.includes('BGE')
                          ? '#F59E0B'
                          : 'rgba(255,255,255,0.5)',
                      }}
                    >
                      {' · RAG: '}
                      {stream.embeddingProvider.includes('BGE') ? 'BGE-M3' : 'Gemini'}
                    </span>
                  )}
                  {stream.isAgent && (
                    <span
                      style={{
                        marginLeft: 6,
                        padding: '1px 6px',
                        borderRadius: 4,
                        background: 'rgba(51,188,225,0.25)',
                        color: '#33BCE1',
                        fontSize: 9,
                        fontWeight: 600,
                        letterSpacing: 0.5,
                      }}
                    >
                      AGENT
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/* 4 header buttons */}
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              {[
                {
                  icon: <IconLightning size={14} />,
                  action: () => setShowQuickActions((v) => !v),
                  title: 'Acciones',
                },
                {
                  icon: <IconClock size={14} />,
                  action: () => setShowHistory((v) => !v),
                  title: 'Historial',
                },
                {
                  icon: <IconPlus size={14} />,
                  action: handleNewConversation,
                  title: 'Nueva conversacion',
                },
                {
                  icon: <IconExpand size={14} expanded={expanded} />,
                  action: () => setExpanded((e) => !e),
                  title: expanded
                    ? (t.via.chat.contraer ?? 'Contraer')
                    : (t.via.chat.expandir ?? 'Expandir'),
                },
                { icon: <IconDash size={14} />, action: () => setOpen(false), title: 'Minimizar' },
              ].map((btn, i) => (
                <button
                  key={i}
                  onClick={btn.action}
                  title={btn.title}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background: 'rgba(255,255,255,0.06)',
                    border: 'none',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 150ms',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                  }}
                >
                  {btn.icon}
                </button>
              ))}
            </div>
          </div>

          {/* ── Quick Actions Overlay ── */}
          {showQuickActions && (
            <div
              ref={quickActionsRef}
              className="via-fadeSlideDown"
              style={{
                position: 'absolute',
                top: 50,
                right: 8,
                background: '#fff',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                border: '1px solid #E5E7EB',
                minWidth: 210,
                zIndex: 20,
                padding: '6px 0',
              }}
            >
              {suggestions.map((s, i) => {
                const SvgIcon = CHIP_ICONS[i % CHIP_ICONS.length];
                return (
                  <button
                    key={i}
                    onClick={() => {
                      submitChip(s.text);
                      setShowQuickActions(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      padding: '8px 12px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 11.5,
                      color: '#374151',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      transition: 'background 150ms',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#F3F4F6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'none';
                    }}
                  >
                    <span style={{ color: '#2F5496', flexShrink: 0 }}>
                      <SvgIcon size={14} />
                    </span>
                    <span>{s.text}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* ── History Overlay ── */}
          {showHistory && (
            <div
              ref={historyRef}
              className="via-fadeSlideDown"
              style={{
                position: 'absolute',
                top: 50,
                right: 8,
                background: '#fff',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                border: '1px solid #E5E7EB',
                minWidth: 210,
                zIndex: 20,
                padding: '16px 20px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1F3864', marginBottom: 8 }}>
                Conversaciones
              </div>
              {historialList.length === 0 ? (
                <p style={{ fontSize: 12, color: '#6B7280' }}>Sin conversaciones previas</p>
              ) : (
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {historialList.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => loadConversacion(conv.id)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderRadius: 6,
                        border: 'none',
                        background:
                          conv.id === activeConversacionId ? 'rgba(47,84,150,0.08)' : 'transparent',
                        cursor: 'pointer',
                        marginBottom: 2,
                        fontSize: 12,
                        color: '#374151',
                        transition: 'background 150ms',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(47,84,150,0.06)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          conv.id === activeConversacionId ? 'rgba(47,84,150,0.08)' : 'transparent';
                      }}
                    >
                      <div style={{ fontWeight: 500, lineHeight: 1.3 }}>
                        {conv.titulo || 'Conversacion sin titulo'}
                      </div>
                      <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>
                        {conv._count.mensajes} mensajes
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── MESSAGES AREA ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', background: '#FAFBFC' }}>
          {/* Welcome screen */}
          {messages.length === 0 && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '0 16px',
              }}
            >
              {/* Logo completo */}
              <img
                src="/img/Logo-horizontal-Color.png"
                alt="VALOR·IA"
                style={{ width: 140, height: 36, objectFit: 'contain', marginBottom: 12 }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/img/logo-color.png';
                }}
              />

              <h2
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#1F3864',
                  margin: 0,
                  lineHeight: 1.3,
                }}
              >
                {t.via.chat.saludo}
              </h2>
              <p
                style={{ fontSize: 13.5, fontWeight: 500, color: '#2F5496', margin: '4px 0 12px' }}
              >
                Tu asistente de gobernanza
              </p>
              <p
                style={{
                  fontSize: 12,
                  color: '#6B7280',
                  textAlign: 'center',
                  maxWidth: 420,
                  marginBottom: 6,
                  lineHeight: 1.5,
                }}
              >
                {t.via.chat.descripcion}
              </p>

              <div style={{ marginBottom: 16 }} />

              {/* Suggestion chips 2x2 */}
              <div style={{ width: '100%', maxWidth: 340 }}>
                <p
                  style={{
                    fontSize: 9.5,
                    textTransform: 'uppercase',
                    color: '#9CA3AF',
                    letterSpacing: 1,
                    fontWeight: 600,
                    marginBottom: 8,
                    textAlign: 'center',
                  }}
                >
                  PRUEBA A PREGUNTAR
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {suggestions.map((suggestion, i) => {
                    const SvgIcon = CHIP_ICONS[i % CHIP_ICONS.length];
                    return (
                      <button
                        key={i}
                        onClick={() => submitChip(suggestion.text)}
                        className="via-chip"
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 8,
                          padding: '10px 10px',
                          textAlign: 'left',
                          border: '1px solid #E5E7EB',
                          borderRadius: 8,
                          background: '#fff',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          transition: 'all 200ms',
                        }}
                      >
                        <span style={{ color: '#2F5496', flexShrink: 0, marginTop: 1 }}>
                          <SvgIcon size={16} />
                        </span>
                        <span style={{ fontSize: 11, color: '#374151', lineHeight: 1.4 }}>
                          {suggestion.text}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Message list */}
          {messages.map((msg) => (
            <div
              key={msg.id}
              className="via-msg-fadeIn"
              style={{
                display: 'flex',
                flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                alignItems: 'flex-start',
                gap: 8,
                marginBottom: 12,
              }}
            >
              {/* Avatar */}
              {msg.role === 'user' ? (
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    flexShrink: 0,
                    background: 'linear-gradient(135deg, #1F3864, #2F5496)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  AG
                </div>
              ) : (
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    flexShrink: 0,
                    background: '#F3F4F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src="/img/Imagotipo-Color.png"
                    alt="V·IA"
                    style={{ width: 20, height: 20, objectFit: 'contain' }}
                  />
                </div>
              )}

              {/* Bubble */}
              <div
                style={{
                  maxWidth: '82%',
                  padding: '8px 12px',
                  borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                  fontSize: 13,
                  lineHeight: 1.5,
                  ...(msg.role === 'user'
                    ? { background: 'linear-gradient(135deg, #1F3864, #2F5496)', color: '#fff' }
                    : { background: '#F9FAFB', border: '1px solid #E5E7EB', color: '#1F2937' }),
                }}
              >
                <div className={msg.role === 'user' ? 'whitespace-pre-wrap' : 'prose-via'}>
                  {renderMessageContent(msg.content, msg.role, navigate)}
                </div>
                {/* Chips de fuentes RAG debajo de respuesta completada */}
                {msg.role === 'assistant' &&
                  msg.id === lastStreamedMsgId &&
                  !stream.isStreaming &&
                  stream.fuentes.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                      {stream.fuentes.map((f, fi) => (
                        <button
                          key={fi}
                          onClick={() => navigate(`/documento/${f.codigo}`)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '2px 8px',
                            borderRadius: 999,
                            border: '1px solid #E5E7EB',
                            background: '#F3F4F6',
                            fontSize: 10,
                            color: '#2F5496',
                            cursor: 'pointer',
                            fontWeight: 500,
                          }}
                          title={f.titulo}
                        >
                          <span
                            style={{
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              background: '#33BCE1',
                            }}
                          />
                          {f.codigo}
                        </button>
                      ))}
                    </div>
                  )}
                {/* Sentinel flags debajo de respuesta completada */}
                {msg.role === 'assistant' &&
                  msg.id === lastStreamedMsgId &&
                  !stream.isStreaming &&
                  stream.sentinelFlags.length > 0 && (
                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {stream.sentinelFlags.map((f, fi) => (
                        <div
                          key={fi}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 4,
                            padding: '3px 8px',
                            borderRadius: 6,
                            fontSize: 10,
                            lineHeight: 1.4,
                            background:
                              f.severity === 'ERROR'
                                ? '#FEF2F2'
                                : f.severity === 'WARNING'
                                  ? '#FFFBEB'
                                  : '#F0F9FF',
                            border: `1px solid ${f.severity === 'ERROR' ? '#FECACA' : f.severity === 'WARNING' ? '#FDE68A' : '#BAE6FD'}`,
                            color:
                              f.severity === 'ERROR'
                                ? '#991B1B'
                                : f.severity === 'WARNING'
                                  ? '#92400E'
                                  : '#0C4A6E',
                          }}
                        >
                          <span style={{ fontWeight: 600, flexShrink: 0 }}>{f.ruleId}</span>
                          <span>{f.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                <div
                  style={{
                    fontSize: 10,
                    marginTop: 4,
                    color: msg.role === 'user' ? 'rgba(255,255,255,0.45)' : '#9CA3AF',
                  }}
                >
                  {msg.timestamp.toLocaleTimeString('es-ES', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>
            </div>
          ))}

          {/* Bubble en vivo durante streaming */}
          {stream.isStreaming && stream.respuestaParcial && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
              <div
                style={{
                  maxWidth: '85%',
                  background: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px 16px 16px 16px',
                  padding: '10px 14px',
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: '#1F2937',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}
              >
                <div
                  key="streaming-bubble"
                  className="prose-via"
                  style={{ animation: 'via-stream-in 0.3s ease-out' }}
                >
                  {renderMessageContent(stream.respuestaParcial, 'assistant', navigate)}
                  <span
                    style={{
                      display: 'inline-block',
                      width: 2,
                      height: 14,
                      background: '#2F5496',
                      marginLeft: 1,
                      verticalAlign: 'text-bottom',
                      animation: 'via-blink 0.8s step-end infinite',
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Thinking indicator — imagotipo girando + texto + dots */}
          {(stream.isThinking || (loading && !stream.respuestaParcial)) && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
              <div
                style={{
                  background: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px 16px 16px 16px',
                  padding: '8px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {/* Imagotipo girando */}
                <img
                  src="/img/Imagotipo-Color.png"
                  alt=""
                  className="via-spin"
                  style={{ width: 18, height: 18, objectFit: 'contain', flexShrink: 0 }}
                />
                {/* Texto */}
                <span
                  style={{
                    fontSize: 11.5,
                    color: '#2F5496',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Analizando gobernanza
                </span>
                {/* 3 dots */}
                <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      style={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        background: '#33BCE1',
                        display: 'inline-block',
                        animation: `via-dot-bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* ── Context change banner ── */}
        {showContextBanner && messages.length > 0 && (
          <div
            style={{
              borderTop: '1px solid #E5E7EB',
              padding: '8px 12px',
              background: '#F8FAFC',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 6,
              }}
            >
              <span style={{ fontSize: 10, color: '#33BCE1', fontWeight: 600 }}>
                {activeDocCodigo
                  ? formatContextLabel(
                      `${activeDocCodigo}${activeDocTitulo ? ` ${activeDocTitulo}` : ''}`,
                    )
                  : entidadCodigo
                    ? formatContextLabel(
                        `${entidadCodigo}${entidadNombre ? ` ${entidadNombre}` : ''}`,
                      )
                    : t.via.nuevoContexto}
              </span>
              <button
                onClick={() => setShowContextBanner(false)}
                style={{
                  fontSize: 12,
                  color: '#aaa',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0 4px',
                  lineHeight: 1,
                  fontFamily: 'inherit',
                }}
              >
                {'\u2715'}
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => {
                    submitChip(s.text);
                    setShowContextBanner(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '5px 8px',
                    borderRadius: 6,
                    border: '1px solid #E5E7EB',
                    background: '#fff',
                    fontSize: 11,
                    color: '#374151',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                >
                  <span style={{ flexShrink: 0, color: '#2F5496' }}>
                    {(() => {
                      const IC = CHIP_ICONS[i % CHIP_ICONS.length];
                      return <IC size={13} />;
                    })()}
                  </span>
                  <span style={{ flex: 1 }}>{s.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── INPUT AREA ── */}
        <div style={{ borderTop: '1px solid #E5E7EB', background: '#fff', flexShrink: 0 }}>
          {/* Context pill */}
          <div style={{ padding: '6px 12px 0' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '3px 10px',
                borderRadius: 999,
                background: 'rgba(51,188,225,0.07)',
                fontSize: 9.5,
                color: '#1A94BF',
              }}
            >
              <IconInfo size={9} />
              <span>{formatContextLabel(contextLabel)}</span>
            </div>
          </div>

          {/* Form */}
          <form
            data-via-form
            onSubmit={handleSend}
            style={{ padding: '8px 12px', display: 'flex', alignItems: 'flex-end', gap: 8 }}
          >
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'flex-end',
                background: '#F9FAFB',
                border: '1.5px solid #E5E7EB',
                borderRadius: 10,
                padding: '6px 10px',
                transition: 'border-color 200ms, box-shadow 200ms',
              }}
              className="via-input-container"
            >
              <textarea
                ref={textareaRef}
                value={input}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                onFocus={(e) => {
                  const container = e.currentTarget.parentElement;
                  if (container) {
                    container.style.borderColor = '#33BCE1';
                    container.style.boxShadow = '0 0 0 3px rgba(51,188,225,0.1)';
                  }
                }}
                onBlur={(e) => {
                  const container = e.currentTarget.parentElement;
                  if (container) {
                    container.style.borderColor = '#E5E7EB';
                    container.style.boxShadow = 'none';
                  }
                }}
                placeholder={t.via.chat.placeholder}
                disabled={loading}
                rows={1}
                style={{
                  flex: 1,
                  resize: 'none',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontSize: 12.5,
                  fontFamily: "'DM Sans', 'Montserrat', sans-serif",
                  lineHeight: 1.4,
                  minHeight: 20,
                  maxHeight: 72,
                  color: '#1F2937',
                }}
              />
            </div>
            {stream.isStreaming ? (
              <button
                type="button"
                onClick={stream.cancelar}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  background: '#EF4444',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'background 200ms',
                }}
                title="Detener"
              >
                <svg width={12} height={12} viewBox="0 0 12 12" fill="#fff">
                  <rect x="1" y="1" width="10" height="10" rx="1.5" />
                </svg>
              </button>
            ) : (
              <button
                type="submit"
                disabled={!input.trim() || loading}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #33BCE1, #2F5496)',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  flexShrink: 0,
                  opacity: input.trim() ? 1 : 0.3,
                  transition: 'opacity 200ms',
                }}
              >
                <IconSend size={14} />
              </button>
            )}
          </form>

          {/* POWERED BY footer */}
          <div
            style={{
              borderTop: '1px solid #F3F4F6',
              padding: '5px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
            }}
          >
            <span style={{ fontSize: 9, color: '#9CA3AF', letterSpacing: 0.5 }}>POWERED BY</span>
            <img
              src="/img/Imagotipo-Color.png"
              alt=""
              style={{ width: 14, height: 14, objectFit: 'contain' }}
            />
            <span style={{ fontSize: 10, fontWeight: 700, color: '#2F5496' }}>VALOR·IA</span>
          </div>
        </div>
      </div>

      {/* ═══ STYLES ═══ */}
      {/* Nota: @keyframes viaSpinFab/viaShineFab y .via-fab/.via-fab:hover viven en index.css
          para garantizar que React 19 no les haga hoisting raro con <style> inline. */}
      <style>{`
        .via-fab.via-open { animation: none; transform: scale(0.9); filter: none; }
        @keyframes viaThinking {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes viaPanelOpen {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes viaMsgFadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes viaFadeSlideDown {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes viaSpin360 {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .via-spin { animation: viaSpin360 1.8s linear infinite; }
        @keyframes via-blink {
          50% { opacity: 0; }
        }
        @keyframes via-stream-in {
          from { opacity: 0.7; }
          to   { opacity: 1; }
        }
        @keyframes via-dot-bounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
        .via-panel-open {
          animation: viaPanelOpen 300ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .via-panel-closed {
          opacity: 0;
          transform: translateY(20px) scale(0.96);
          pointer-events: none;
        }
        .via-msg-fadeIn {
          animation: viaMsgFadeIn 300ms ease-out;
        }
        .via-fadeSlideDown {
          animation: viaFadeSlideDown 200ms ease-out;
        }
        .via-resize-handle {
          background: transparent;
          transition: background 150ms;
        }
        .via-resize-handle:hover {
          background: rgba(51, 188, 225, 0.3);
        }
        .via-chip:hover {
          border-color: #33BCE1 !important;
          background: rgba(51, 188, 225, 0.03) !important;
          box-shadow: 0 2px 8px rgba(51, 188, 225, 0.1);
          transform: translateY(-1px);
        }
        .prose-via { line-height: 1.5; }
        .prose-via > *:first-child { margin-top: 0; }
        .prose-via > *:last-child { margin-bottom: 0; }
        @media (prefers-reduced-motion: reduce) {
          .via-panel *, .via-spin {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      {/* ═══ FAB ═══ */}
      {!open && (
        <button
          data-testid="chat-fab"
          onClick={() => setOpen(true)}
          className={`via-fab fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full bg-[#F8F8F8] flex items-center justify-center overflow-visible border-none cursor-pointer`}
          title={t.via.titulo}
          style={{
            position: 'fixed',
            animation: 'viaSpinFab 6s cubic-bezier(0.65, 0, 0.35, 1) infinite',
          }}
        >
          <img
            src="/img/Imagotipo-Color.png"
            alt="V·IA"
            style={{ width: '80%', height: '80%', objectFit: 'contain' }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/img/Imagotipo-blanco.png';
            }}
          />
          {/* Badge dot when has messages and closed */}
          {messages.length > 0 && (
            <span
              style={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: '#33BCE1',
                border: '2px solid #F8F8F8',
              }}
            />
          )}
        </button>
      )}
    </>
  );
}
